// static/table_engine_src/core/selection/nav.js

export function createSelectionNav(ctx, { cols, rowEls, tdAt } = {}) {
  const allCols = Array.isArray(cols) ? cols : [];
  const listRows = typeof rowEls === "function" ? rowEls : () => [];
  const getTd =
    typeof tdAt === "function"
      ? tdAt
      : (tr, cIdx) => {
          if (!tr) return null;
          return tr.children?.[cIdx] || null;
        };

  function isHiddenOrNonDataCol(cIdx) {
    const col = allCols[cIdx];
    if (!col) return true;
    const k = col.key;
    if (!k) return true;

    if (ctx.hiddenCols && typeof ctx.hiddenCols.has === "function" && ctx.hiddenCols.has(k)) {
      return true;
    }

    const kind = String(col.kind || "").toLowerCase();
    if (kind === "rowhandle" || kind === "actions") return true;

    return false;
  }

  function nearestNavigableCol(startIdx, dir /* -1,0,+1 */) {
    const n = allCols.length;
    if (!n) return 0;

    const tryDir = (d) => {
      let i = startIdx;
      while (i >= 0 && i < n) {
        if (!isHiddenOrNonDataCol(i)) return i;
        i += d;
      }
      return -1;
    };

    const s = Math.max(0, Math.min(n - 1, startIdx));
    if (dir < 0) {
      const left = tryDir(-1);
      if (left >= 0) return left;
      const right = tryDir(+1);
      return right >= 0 ? right : 0;
    }
    if (dir > 0) {
      const right = tryDir(+1);
      if (right >= 0) return right;
      const left = tryDir(-1);
      return left >= 0 ? left : n - 1;
    }

    if (!isHiddenOrNonDataCol(s)) return s;
    const r = tryDir(+1);
    if (r >= 0) return r;
    const l = tryDir(-1);
    return l >= 0 ? l : 0;
  }

  function nextNavigableCol(cIdx, dc) {
    let c = cIdx;
    if (dc > 0) c = Math.min(allCols.length - 1, cIdx + 1);
    else if (dc < 0) c = Math.max(0, cIdx - 1);
    else return nearestNavigableCol(cIdx, 0);

    return nearestNavigableCol(c, dc > 0 ? +1 : -1);
  }

  function firstNavigableCol() {
    return nearestNavigableCol(0, +1);
  }

  function lastNavigableCol() {
    return nearestNavigableCol(Math.max(0, allCols.length - 1), -1);
  }

  function clampRC(r, c) {
    const rows = listRows();
    if (!rows.length) return { r: 0, c: nearestNavigableCol(0, +1) };

    r = Math.max(0, Math.min(rows.length - 1, r));
    c = Math.max(0, Math.min(allCols.length - 1, c));
    c = nearestNavigableCol(c, 0);

    return { r, c };
  }

  // Basic "cell has value" for Ctrl+Arrow jumping.
  function cellHasValue(r, c) {
    const rows = listRows();
    const tr = rows[r];
    if (!tr) return false;

    const col = allCols[c];
    const colKey = col && col.key ? col.key : null;
    const td = getTd(tr, c);
    if (!td) {
      if (!colKey) return false;
      if (col?.kind === "input") {
        const el0 = ctx.getInput?.(tr, colKey);
        if (!el0) return false;
        if (el0.tagName === "SELECT") return String(el0.value ?? "").trim() !== "";
        const raw0 = el0.dataset && el0.dataset.raw != null ? String(el0.dataset.raw).trim() : "";
        const v0 = String(el0.value ?? "").trim();
        return raw0 !== "" || v0 !== "";
      }
      if (col?.kind === "output") {
        const out0 = ctx.getOut?.(tr, colKey);
        const rawOut0 = out0 && out0.dataset && out0.dataset.raw != null ? String(out0.dataset.raw).trim() : "";
        if (rawOut0 !== "") return true;
        const txt0 = String(out0?.textContent || "").trim();
        return txt0 !== "" && txt0 !== "N/A";
      }
      return false;
    }

    const el = td.querySelector("input, textarea, select");
    if (el) {
      if (el.tagName === "SELECT") return String(el.value ?? "").trim() !== "";
      const raw = el.dataset && el.dataset.raw != null ? String(el.dataset.raw).trim() : "";
      const v = String(el.value ?? "").trim();
      return raw !== "" || v !== "";
    }

    const out = td.querySelector("[data-out]");
    const rawOut = out && out.dataset && out.dataset.raw != null ? String(out.dataset.raw).trim() : "";
    if (rawOut !== "") return true;

    const txt = String(td.textContent || "").trim();
    if (txt === "" || txt === "N/A") return false;
    return true;
  }

  return {
    isHiddenOrNonDataCol,
    nearestNavigableCol,
    nextNavigableCol,
    firstNavigableCol,
    lastNavigableCol,
    clampRC,
    cellHasValue,
  };
}
